<script lang="ts" setup>
import { ref, computed } from 'vue'

interface EventsItem {
  timestamp: string | Date | null
  runNumber: number
  holeNo: number
  recipeNumber: number
  recipeName: string
  eventUnit: string
  eventType: string
  totalCement1: number
  totalCement2: number
  totalSlurry: number
  totalAdditive1: number
  totalAdditive2: number
  totalAccelerator: number
  totalColloidalSilica: number
  totalColloidalAccelerator: number
}

const props = defineProps({
  events: {
    type: Array as () => EventsItem[],
    required: true,
    default: () => []
  }
})

const searchQuery = ref('')

const formatDate = (date: any) => {
  if (!date) return "N/A";
  const d = new Date(date);
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const fmt = (val: number | undefined | null) => {
  if (val === undefined || val === null) return '0.0'
  return val.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
}

function getEventUnitColor(type: string) {
  const t = (type || '').toLowerCase();
  if (t.includes('pump 1')) return 'info';
  if (t.includes('pump 2')) return 'success';
  if (t.includes('pump 3')) return 'error';
  if (t.includes('pump 4')) return 'warning';
  return 'default';
}

function getEventTypeColor(type: string) {
  const t = (type || '').toLowerCase();
  if (t.includes('emergency')) return 'error';
  if (t.includes('reached')) return 'success';
  if (t.includes('pump start') || t.includes('pump stop')) return 'secondary';
  if (t.includes('hole change')) return 'info';
  if (t.includes('low level')) return 'warning';
  return 'default';
}

function calculateIncrementStats(values: number[]) {
  const increments: number[] = []
  
  for (let i = 1; i < values.length; i++) {
    const diff = values[i]! - values[i - 1]!
    if (diff > 0) {
      increments.push(diff)
    }
  }
  
  if (increments.length === 0) return { avg: 0, max: 0 }
  
  const sum = increments.reduce((a, b) => a + b, 0)
  return {
    avg: sum / increments.length,
    max: Math.max(...increments)
  }
}

// Oppdatert filter-logikk for å søke i RecipeName eller HoleNo
const filteredEvents = computed(() => {
  if (!searchQuery.value) return props.events
  
  const q = searchQuery.value.toLowerCase()
  return props.events.filter(evt => 
    (evt.recipeName || '').toLowerCase().includes(q) || 
    evt.holeNo?.toString().includes(q) ||
    evt.runNumber?.toString().includes(q)
  )
})
const cement1Stats = computed(() => calculateIncrementStats(filteredEvents.value.map(e => e.totalCement1)))
const cement2Stats = computed(() => calculateIncrementStats(filteredEvents.value.map(e => e.totalCement2)))



const headers = [
  { title: 'Timestamp', key: 'timestamp', width: '160px' },
  { title: 'Hole No', key: 'holeNo', align: 'start' as const },
  { title: 'Unit', key: 'eventUnit' },
  { title: 'Event type', key: 'eventType' },
  { title: 'Cement 1/2', key: 'cement', align: 'end' as const },
  { title: 'Slurry', key: 'totalSlurry', align: 'end' as const },
  { title: 'Add. 1', key: 'totalAdditive1', align: 'end' as const },
  { title: 'Add. 2', key: 'totalAdditive2', align: 'end' as const },
  { title: 'Accel.', key: 'totalAccelerator', align: 'end' as const },
  { title: 'Coll. Si', key: 'totalColloidalSilica', align: 'end' as const },
  { title: 'Coll. Acc.', key: 'totalColloidalAccelerator', align: 'end' as const },
];
</script>

<template>
  <v-container fluid>
    <v-row class="mb-6">
      <v-col cols="12" md="6">
        <v-card class="h-100" elevation="2">
          <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
            Cement 1 Stats
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-6">
            <div class="d-flex justify-space-between align-center mb-4 border-b pb-2">
              <span class="text-body-2 text-medium-emphasis font-weight-bold text-uppercase">Avg Increment</span>
              <span class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(cement1Stats.avg) }} <span class="text-caption text-medium-emphasis">kg</span></span>
            </div>
            <div class="d-flex justify-space-between align-center">
              <span class="text-body-2 text-medium-emphasis font-weight-bold text-uppercase">Max Increment</span>
              <span class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(cement1Stats.max) }} <span class="text-caption text-medium-emphasis">kg</span></span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="h-100" elevation="2">
          <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
            Cement 2 Stats
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-6">
            <div class="d-flex justify-space-between align-center mb-4 border-b pb-2">
              <span class="text-body-2 text-medium-emphasis font-weight-bold text-uppercase">Avg Increment</span>
              <span class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(cement2Stats.avg) }} <span class="text-caption text-medium-emphasis">kg</span></span>
            </div>
            <div class="d-flex justify-space-between align-center">
              <span class="text-body-2 text-medium-emphasis font-weight-bold text-uppercase">Max Increment</span>
              <span class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(cement2Stats.max) }} <span class="text-caption text-medium-emphasis">kg</span></span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card elevation="2">
      <v-card-title class="d-flex justify-space-between align-center py-3 px-4 bg-grey-lighten-4">
        <span class="text-subtitle-1 font-weight-bold">Recipe & Material Log ({{ filteredEvents.length }})</span>
        <v-text-field
          v-model="searchQuery"
          placeholder="Filter by Recipe or Hole..."
          density="compact"
          variant="outlined"
          hide-details
          prepend-inner-icon="mdi-magnify"
          style="min-width: 250px;"
          bg-color="white"
        ></v-text-field>
      </v-card-title>
      <v-divider></v-divider>
      
      <v-data-table
        :headers="headers"
        :items="filteredEvents"
        density="compact"
        class="text-body-2"
        hover
      >
        <template v-slot:item.timestamp="{ item }">
          <span class="text-medium-emphasis">{{ formatDate(item.timestamp) }}</span>
        </template>
        
        <template v-slot:item.holeNo="{ item }">
          <v-chip size="small" variant="flat" color="blue-lighten-5" class="text-blue-darken-2 font-weight-bold">
            {{ item.holeNo }}
          </v-chip>
        </template>

        <template v-slot:item.eventUnit="{ item }">
          <v-chip :color="getEventUnitColor(item.eventUnit)" size="small" variant="tonal" class="font-weight-bold">
            {{ item.eventUnit }}
          </v-chip>
        </template>

        <template v-slot:item.eventType="{ item }">
          <v-chip
            :color="getEventTypeColor(item.eventType)"
            size="small"
            variant="tonal"
            class="font-weight-bold text-uppercase"
            label
          >
            {{ item.eventType }}
          </v-chip>
        </template>

        <template v-slot:item.cement="{ item }">
          <span class="font-monospace">{{ fmt(item.totalCement1) }} / {{ fmt(item.totalCement2) }}</span>
        </template>
        
        <template v-slot:item.totalSlurry="{ item }"><span class="font-monospace">{{ fmt(item.totalSlurry) }}</span></template>
        <template v-slot:item.totalAdditive1="{ item }"><span class="font-monospace">{{ fmt(item.totalAdditive1) }}</span></template>
        <template v-slot:item.totalAdditive2="{ item }"><span class="font-monospace">{{ fmt(item.totalAdditive2) }}</span></template>
        <template v-slot:item.totalAccelerator="{ item }"><span class="font-monospace">{{ fmt(item.totalAccelerator) }}</span></template>
        <template v-slot:item.totalColloidalSilica="{ item }"><span class="font-monospace">{{ fmt(item.totalColloidalSilica) }}</span></template>
        <template v-slot:item.totalColloidalAccelerator="{ item }"><span class="font-monospace">{{ fmt(item.totalColloidalAccelerator) }}</span></template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
.font-monospace {
  font-family: 'Courier New', Courier, monospace !important;
}
</style>